
import { useState, useCallback } from 'react';
import { FAQData } from '@/components/faq-serp/types';
import { type CrawlResult, type EnhancedCrawlResult } from '@/types/crawler';
import { generateFaqsForCrawlResult, generateFaqsForUrl } from './utils';
import { toast } from 'sonner';

interface UseFaqExpertOptions {
  onSuccess?: (data: FAQData) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for managing the FAQ Expert feature
 */
export function useFaqExpert(options?: UseFaqExpertOptions) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [faqData, setFaqData] = useState<FAQData | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);

  /**
   * Generate FAQs for a crawl result
   */
  const generateFaqsForResult = useCallback(async (
    result: CrawlResult | EnhancedCrawlResult | null
  ) => {
    if (!result) {
      const error = new Error('No crawl result provided');
      toast.error(error.message);
      setError(error);
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await generateFaqsForCrawlResult(result);
      
      if (response && response.faq) {
        setFaqData(response.faq);
        setCurrentUrl(result?.pages?.[0]?.url || null);
        
        if (options?.onSuccess) {
          options.onSuccess(response.faq);
        }
      } else {
        throw new Error("Failed to generate FAQs: No data returned");
      }
    } catch (error) {
      console.error("Error generating FAQs:", error);
      setError(error as Error);
      
      if (options?.onError) {
        options.onError(error as Error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  /**
   * Generate FAQs for a specific URL
   */
  const generateFaqsForUrlCallback = useCallback(async (url: string) => {
    if (!url) {
      const error = new Error('Please enter a valid URL');
      toast.error(error.message);
      setError(error);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setCurrentUrl(url);

    try {
      const response = await generateFaqsForUrl(url);
      
      if (response && response.faq) {
        setFaqData(response.faq);
        
        if (options?.onSuccess) {
          options.onSuccess(response.faq);
        }
      } else {
        throw new Error("Failed to generate FAQs: No data returned");
      }
    } catch (error) {
      console.error("Error generating FAQs for URL:", error);
      setError(error as Error);
      
      if (options?.onError) {
        options.onError(error as Error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setFaqData(null);
    setError(null);
    setIsLoading(false);
    setCurrentUrl(null);
  }, []);

  return {
    isLoading,
    error,
    faqData,
    currentUrl,
    generateFaqsForResult,
    generateFaqsForUrl: generateFaqsForUrlCallback,
    reset
  };
}
